import { useState } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { MoreHorizontalIcon, ShieldIcon, Trash2Icon } from "lucide-react";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { ChangeRoleDialog } from "@/features/members/components/ChangeRoleDialog";
import { RoleBadge } from "@/features/members/components/RoleBadge";
import LeaveProjectForm from "@/features/projects/components/LeaveProjectForm";
import { RemoveMemberSchema } from "@/features/projects/schemas";
import { removeProjectMember } from "@/features/projects/services/project";
import { Project, ProjectMember } from "@/features/projects/types";
import Error from "@/shared/components/Error";
import { PermissionTooltip } from "@/shared/components/PermissionTooltip";
import { QUERY_KEYS } from "@/shared/lib/constants/queries";
import { formatAbsoluteDateTime, formatRelativeDate } from "@/shared/lib/utils/date";
import { AppError } from "@/shared/lib/utils/errors";

interface Props {
  member: ProjectMember;
  project: Project;
  isCurrentUser: boolean;
  canRemoveMembers: boolean;
  canChangeRoles: boolean;
  canLeaveProject: boolean;
}

export function MemberRow({ member, project, isCurrentUser, canRemoveMembers, canChangeRoles, canLeaveProject }: Props) {
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const role = member.role.name;
  const initials = `${member.firstName[0] ?? ""}${member.lastName[0] ?? ""}`.toUpperCase();
  const fullName = `${member.firstName} ${member.lastName}`;
  const joinedAt = formatRelativeDate(member.joinedAt);
  const joinedAtAbsolute = formatAbsoluteDateTime(member.joinedAt);

  const removeMutation = useMutation<undefined, AppError | Error, RemoveMemberSchema>({
    mutationFn: removeProjectMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_MEMBERS, project.id], exact: true });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_PROJECT_BY_ID, project.id], exact: true });
      setRemoveDialogOpen(false);
    },
  });

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar size="default">
            <AvatarImage src="" alt={fullName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{fullName}</span>
              {isCurrentUser && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  You
                </Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground truncate">{member.emailAddress}</span>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <RoleBadge role={role} />
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="tabular-nums font-medium">{member.totalTicketCount}</span>
            <span className="text-muted-foreground">total</span>
          </div>
          <div className="h-1.5 w-24 bg-muted overflow-hidden">{member.totalTicketCount > 0 && <div className="h-full bg-primary/60" style={{ width: `${(member.closedTicketCount / member.totalTicketCount) * 100}%` }} />}</div>
          <p className="text-[10px] text-muted-foreground">
            {member.closedTicketCount} closed · {member.openTicketCount} open
          </p>
        </div>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        <Tooltip delayDuration={400}>
          <TooltipTrigger asChild>
            <span className="w-fit">{joinedAt}</span>
          </TooltipTrigger>
          <TooltipContent>{joinedAtAbsolute}</TooltipContent>
        </Tooltip>
      </TableCell>
      <TableCell>
        {isCurrentUser ? (
          <PermissionTooltip allowed={canLeaveProject} message="This account isn't able to leave the project.">
            <LeaveProjectForm project={project} />
          </PermissionTooltip>
        ) : (
          <>
            {removeMutation.isError && <Error title="Unable to remove member" error={removeMutation.error} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="text-muted-foreground">
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuLabel className="normal-case tracking-normal text-xs font-normal text-muted-foreground">{fullName}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <PermissionTooltip allowed={canChangeRoles} message="You don't have permission to change member roles.">
                  {canChangeRoles ? (
                    <ChangeRoleDialog member={member} projectId={project.id}>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        <ShieldIcon />
                        Change role
                      </DropdownMenuItem>
                    </ChangeRoleDialog>
                  ) : (
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <ShieldIcon />
                      Change role
                    </DropdownMenuItem>
                  )}
                </PermissionTooltip>
                <DropdownMenuSeparator />
                <PermissionTooltip allowed={canRemoveMembers} message="You don't have permission to remove members from this project.">
                  {canRemoveMembers ? (
                    <AlertDialog
                      open={removeDialogOpen}
                      onOpenChange={(next) => {
                        if (!removeMutation.isPending) {
                          setRemoveDialogOpen(next);
                          if (!next) removeMutation.reset();
                        }
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem variant="destructive" onSelect={(e) => e.preventDefault()}>
                          <Trash2Icon />
                          Remove from project
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {fullName}?</AlertDialogTitle>
                          <AlertDialogDescription>They will immediately lose access to this project and all its tickets. You can re-invite them at any time.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={removeMutation.isPending}>Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40" disabled={removeMutation.isPending} onClick={() => removeMutation.mutate({ projectId: project.id, memberId: member.id })}>
                            <Trash2Icon />
                            {removeMutation.isPending ? "Removing..." : "Remove member"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <DropdownMenuItem variant="destructive" onSelect={(e) => e.preventDefault()}>
                      <Trash2Icon />
                      Remove from project
                    </DropdownMenuItem>
                  )}
                </PermissionTooltip>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
